import { GetServerSidePropsContext } from 'next';
import { get, post } from '../../networking';
import {
    GetPricesParams,
    PricesResponse,
    StripeCoupon,
    StripeProvider,
    ReactivateResponse,
    CreateCustomerPortalSessionResponse,
    CreateCheckoutPayload,
    CreateCheckoutResponse,
    CreateNoCcCheckoutPayload,
    CreateExtendTrialCheckoutPayload,
    CreateAddPaymentMethodCheckoutPayload,
    CreateImmediateUpgradeCheckoutPayload,
    StripePrice,
    RecurringPricesResponse,
    CheckoutSessionType,
    GetPricesRecurringParams,
    GetPricesOneTimeParams,
    TempCheckoutInfoLocalStorage,
    TempCheckoutInfoSetPayload,
    TempCreateCheckoutMap,
    ComposedRecurringPrices,
    PriceRecurringInterval
} from './types/stripe.type';
import LocalStorage from '../../lib/local-storage';
import { PlanKey, PlanTier } from './types/payments.type';

export const getCouponByID = async (id?: string, context?: GetServerSidePropsContext): Promise<StripeCoupon> => {
    if (!id) {
        return;
    }
    return await get(`stripe/coupon/${id}`, {}, context);
};

export const getRecurringPrices = async (
    params: GetPricesRecurringParams,
    context?: GetServerSidePropsContext
): Promise<PricesResponse> => {
    const { provider, key, tier, coupon, trialDays, variant, currency } = params;
    /**
     * Doing this to mimic the existing backend logic. Looks like we still use checkout info for the
     * coupon and nothing else. I believe we should prioritize the coupon that's passed in as a param
     * over the one in checkout info.
     */
    const checkoutInfo = getCheckoutInfoStorage();
    const appliedCoupon = coupon || checkoutInfo?.coupons;
    return await get(
        `stripe/prices?provider=${provider || 'sunnyside'}&key=${key}&tier=${tier}&coupon=${appliedCoupon}&trialDays=${trialDays}&variant=${variant}&currency=${currency}`,
        {},
        context
    );
};

/**
 * This is solely used when we call the 'payments-gateway/stripe/prices' endpoint to mimic the
 * expected behavior of the old backend.
 *
 * PLEASE DO NOT USE UNLESS MAKING THE getRecurringPrices CALL BACKWARDS COMPATIBLE WITH THE
 * 'payments-gateway/stripe/prices' ENDPOINT.
 */
export const tempConvertRecurringPricesParamsBasedOnCheckoutInfo = (params: GetPricesRecurringParams) => {
    const checkoutInfo = getCheckoutInfoStorage();
    const getTier = (): PlanTier => {
        if (checkoutInfo?.price_group?.startsWith('prem') || checkoutInfo?.tier === 'PREMIUM_COACHING') {
            return 'premium';
        }
        return params.tier;
    };
    const tier = getTier();
    const getVariant = (): string => {
        if (tier === 'premium') {
            switch (checkoutInfo?.price_group) {
            case 'prem_198': {
                return '1';
            }
            case 'prem_298': {
                return '2';
            }
            case 'prem_398': {
                return '3';
            }
            default: {
                return params.variant;
            }
            }
        }
        return params.variant;
    };
    const variant = getVariant();
    const coupon = params?.coupon || checkoutInfo?.coupons;
    const trialDays = params?.trialDays ?? checkoutInfo?.trial_days;
    return {
        ...params,
        tier,
        variant,
        coupon,
        trialDays
    };
};

export const getOneTimePrices = async (
    params: GetPricesOneTimeParams,
    context?: GetServerSidePropsContext
): Promise<PricesResponse> => {
    const { provider, key, variant, coupon, currency } = params;
    /**
     * Doing this to mimic the existing backend logic. Looks like we still use checkout info for the
     * coupon and nothing else. I believe we should prioritize the coupon that's passed in as a param
     * over the one in checkout info.
     */
    const checkoutInfo = getCheckoutInfoStorage();
    const appliedCoupon = coupon || checkoutInfo?.coupons;
    return await get(`stripe/prices?provider=${provider || 'sunnyside'}&key=${key}&variant=${variant}&coupon=${appliedCoupon}&currency=${currency}`, {}, context);
};

export const reactivate = async (
    provider: StripeProvider = 'sunnyside',
    context?: GetServerSidePropsContext
): Promise<ReactivateResponse> => {
    return await post(`stripe/reactivate?provider=${provider}`, {}, {}, context) as ReactivateResponse;
};

export const createCustomerPortalSession = async (
    provider: StripeProvider = 'sunnyside',
    returnUrl?: string,
    context?: GetServerSidePropsContext
): Promise<CreateCustomerPortalSessionResponse> => {
    return await get(`stripe/create-customer-portal-session?provider=${provider}&returnUrl=${returnUrl}`, {}, context);
};

/**
 * This is to mimic the existing backend logic. Looks like we still use checkout info for the
 * coupon and trial days when creating a checkout session. I believe we should prioritize the
 * coupon and trial days that's passed in with the payload over the one in checkout info.
 */
const applyCheckoutInfoToCheckoutPayload = (body: {
    coupons?: string[],
    trialDays?: number
}): Record<string, any> => {
    const checkoutInfo = getCheckoutInfoStorage();
    const coupons = body.coupons || [checkoutInfo?.coupons];
    const trialDays = body.trialDays ?? checkoutInfo?.trial_days;
    return {
        ...body,
        coupons,
        trialDays
    };
};

export const createCheckout = async (
    provider: StripeProvider = 'sunnyside',
    body: CreateCheckoutPayload,
    context?: GetServerSidePropsContext
): Promise<CreateCheckoutResponse> => {
    const payload = applyCheckoutInfoToCheckoutPayload(body);
    return await post(`stripe/checkout?provider=${provider}`, payload, {}, context);
};

export const createNoCcCheckout = async (
    provider: StripeProvider = 'sunnyside',
    body: CreateNoCcCheckoutPayload,
    context?: GetServerSidePropsContext
): Promise<CreateCheckoutResponse> => {
    const payload = applyCheckoutInfoToCheckoutPayload(body);
    return await post(`stripe/checkout/no-cc?provider=${provider}`, payload, {}, context);
};

export const createExtendTrialCheckout = async (
    provider: StripeProvider = 'sunnyside',
    body: CreateExtendTrialCheckoutPayload,
    context?: GetServerSidePropsContext
): Promise<CreateCheckoutResponse> => {
    const payload = applyCheckoutInfoToCheckoutPayload(body);
    return await post(`stripe/checkout/extend-trial?provider=${provider}`, payload, {}, context);
};

export const createAddPaymentMethodCheckout = async (
    provider: StripeProvider = 'sunnyside',
    body: CreateAddPaymentMethodCheckoutPayload,
    context?: GetServerSidePropsContext
): Promise<CreateCheckoutResponse> => {
    return await post(`stripe/checkout/add-payment-method?provider=${provider}`, body, {}, context);
};

export const createImmediateUpgradeCheckout = async (
    provider: StripeProvider = 'sunnyside',
    body: CreateImmediateUpgradeCheckoutPayload,
    context?: GetServerSidePropsContext
): Promise<CreateCheckoutResponse> => {
    const payload = applyCheckoutInfoToCheckoutPayload(body);
    return await post(`stripe/checkout/immediate-upgrade?provider=${provider}`, payload, {}, context);
};

export const getCurrencyList = async (
    provider: StripeProvider = 'sunnyside',
    context?: GetServerSidePropsContext
): Promise<string[]> => {
    return await get(`stripe/currency/list?provider=${provider}`, {}, context);
};

export const getUserCurrency = async (
    provider: StripeProvider = 'sunnyside',
    context?: GetServerSidePropsContext
): Promise<string> => {
    return await get(`stripe/currency/user?provider=${provider}`, {}, context);
};

export const getStripeSubscriptions = async (
    provider: StripeProvider = 'sunnyside',
    context?: GetServerSidePropsContext
): Promise<any> => {
    return await get(`stripe/subscriptions?provider=${provider}`, {}, context);
};

type OldComputedPrice = {
    price_id: string,
    plan_price: number,
    plan_price_with_discount: number,
    plan_price_per_month: number,
    plan_price_per_month_with_discount: number,
    symbol: string,
    suffix: string
};

type OldPricesResponse = {
    prices: StripePrice[],
    product: Record<string, any>,
    coupons?: StripeCoupon[],
    trial_days: number,
    checkout_info: Record<string, any>,
    display_currency_selector: boolean,
    computed_prices?: {
        annual_plan_price: number,
        annual_plan_price_with_discount: number,
        annual_plan_price_per_month: number,
        annual_plan_price_per_month_with_discount: number,
        quarterly_plan_price: number,
        quarterly_plan_price_with_discount: number,
        quarterly_plan_price_per_month: number,
        quarterly_plan_price_per_month_with_discount: number,
        monthly_plan_price: number,
        monthly_plan_price_with_discount: number,
        monthly_savings: number,
        symbol: string,
        suffix: string
    },
    computed_price_list?: OldComputedPrice[]
};

export const convertOldCheckoutRequestToV2 = <T extends keyof TempCreateCheckoutMap>(
    priceId: string,
    planKey: PlanKey = 'ss_digital',
    provider: StripeProvider = 'sunnyside',
    successUrl?: string, 
    cancelUrl?: string,
    checkoutType?: T | string
): Promise<CreateCheckoutResponse> => {
    const createPayload = (): TempCreateCheckoutMap[T] => {
        switch (checkoutType) {
        case CheckoutSessionType.IMMEDIATE_UPGRADE: {
            const payload = {
                price: {
                    priceId,
                    type: 'recurring'
                },
                successUrl
            } as TempCreateCheckoutMap[CheckoutSessionType.IMMEDIATE_UPGRADE];
            return payload as TempCreateCheckoutMap[T];
        }
        case CheckoutSessionType.NO_CC: {
            const payload = {} as TempCreateCheckoutMap[CheckoutSessionType.NO_CC];
            return payload as TempCreateCheckoutMap[T];
        }
        case CheckoutSessionType.ADD_PAYMENT: {
            const payload = {
                successUrl,
                cancelUrl,
                planKey
            } as TempCreateCheckoutMap[CheckoutSessionType.ADD_PAYMENT];
            return payload as TempCreateCheckoutMap[T];
        }
        case CheckoutSessionType.EXTEND_TRIAL: {
            const payload = {
                successUrl,
                planKey
            } as TempCreateCheckoutMap[CheckoutSessionType.EXTEND_TRIAL];
            return payload as TempCreateCheckoutMap[T];
        }
        case CheckoutSessionType.PREMIUM_COACHING: {
            const payload = {
                price: {
                    priceId,
                    type: 'recurring'
                },
                successUrl,
                preserveTrial: true
            } as TempCreateCheckoutMap[CheckoutSessionType.PREMIUM_COACHING];
            return payload as TempCreateCheckoutMap[T];
        }
        default: {
            const payload = {
                prices: {
                    recurring: [{
                        priceId,
                        type: 'recurring'
                    }]
                },
                successUrl,
                cancelUrl
            } as TempCreateCheckoutMap[CheckoutSessionType.CHOOSE_PLAN];
            return payload as TempCreateCheckoutMap[T];
        }
        }
    };
    const payload = createPayload();

    const createSession = async <T extends keyof TempCreateCheckoutMap>(
        provider: StripeProvider = 'sunnyside',
        checkoutType: T = undefined,
        payload: TempCreateCheckoutMap[T],
    ): Promise<CreateCheckoutResponse> => {
        switch (checkoutType) {
        case CheckoutSessionType.SWITCH_PLAN:
        case CheckoutSessionType.IMMEDIATE_UPGRADE: {
            return createImmediateUpgradeCheckout(provider, payload as CreateImmediateUpgradeCheckoutPayload);
        }
        case CheckoutSessionType.NO_CC: {
            return createNoCcCheckout(provider, payload as CreateNoCcCheckoutPayload);
        }
        case CheckoutSessionType.ADD_PAYMENT: {
            return createAddPaymentMethodCheckout(provider, payload as CreateAddPaymentMethodCheckoutPayload);
        }
        case CheckoutSessionType.EXTEND_TRIAL: {
            return createExtendTrialCheckout(provider, payload as CreateExtendTrialCheckoutPayload);
        }
        case CheckoutSessionType.SWITCH_TRIAL:
        case CheckoutSessionType.PREMIUM_COACHING: {
            return createImmediateUpgradeCheckout(provider, {
                ...payload,
                preserveTrial: true
            } as CreateImmediateUpgradeCheckoutPayload);
        }
        default: {
            return createCheckout(provider, payload as CreateCheckoutPayload);
        }
        }
    };
    return createSession(provider, checkoutType as T, payload);
};

export const convertOldPricesToV2 = (
    oldPrices: OldPricesResponse
): RecurringPricesResponse => {
    const prices = oldPrices.prices;
    const coupons = oldPrices.coupons;
    const trialDays = oldPrices.trial_days;

    const computedPrices = oldPrices.computed_prices;

    if (computedPrices) {
        const annualPrice = prices.find(price => price.recurring.interval === 'year' && price.recurring.interval_count === 1);
        const monthlyPrice = prices.find(price => price.recurring.interval === 'month' && price.recurring.interval_count === 1);
        const quarterlyPrice = prices.find(price => price.recurring.interval === 'month' && price.recurring.interval_count === 3);
        return {
            type: 'recurring',
            prices: prices,
            currency: annualPrice?.currency ?? monthlyPrice?.currency ?? quarterlyPrice?.currency,
            coupons,
            trialDays,
            symbol: computedPrices.symbol,
            suffix: computedPrices.suffix,
            uiConfig: {
                displayCurrencySelector: oldPrices.display_currency_selector
            },
            composedPrices: {
                annual: annualPrice ? {
                    price: computedPrices.annual_plan_price,
                    priceWithDiscount: computedPrices.annual_plan_price_with_discount,
                    pricePerMonth: computedPrices.annual_plan_price_per_month,
                    pricePerMonthWithDiscount: computedPrices.annual_plan_price_per_month_with_discount,
                    symbol: computedPrices.symbol,
                    suffix: computedPrices.suffix,
                    priceId: annualPrice?.id,
                    monthlySavings: computedPrices.monthly_savings
                } : undefined,
                monthly: monthlyPrice ? {
                    price: computedPrices.monthly_plan_price,
                    priceWithDiscount: computedPrices.monthly_plan_price_with_discount,
                    pricePerMonth: computedPrices.monthly_plan_price,
                    pricePerMonthWithDiscount: computedPrices.monthly_plan_price_with_discount,
                    symbol: computedPrices.symbol,
                    suffix: computedPrices.suffix,
                    priceId: monthlyPrice?.id
                } : undefined,
                quarterly: quarterlyPrice ? {
                    price: computedPrices.quarterly_plan_price,
                    priceWithDiscount: computedPrices.quarterly_plan_price_with_discount,
                    pricePerMonth: computedPrices.quarterly_plan_price_per_month,
                    pricePerMonthWithDiscount: computedPrices.quarterly_plan_price_per_month_with_discount,
                    symbol: computedPrices.symbol,
                    suffix: computedPrices.suffix,
                    priceId: quarterlyPrice?.id
                } : undefined
            }
        };
    }

    const computedPriceList = oldPrices.computed_price_list;
    const annualStripePrice = prices.find(price => price.recurring.interval === 'year' && price.recurring.interval_count === 1);
    const monthlyStripePrice = prices.find(price => price.recurring.interval === 'month' && price.recurring.interval_count === 1);
    const quarterlyStripePrice = prices.find(price => price.recurring.interval === 'month' && price.recurring.interval_count === 3);

    const annualPrice = computedPriceList.find(price => price.price_id === annualStripePrice?.id);
    const monthlyPrice = computedPriceList.find(price => price.price_id === monthlyStripePrice?.id);
    const quarterlyPrice = computedPriceList.find(price => price.price_id === quarterlyStripePrice?.id);

    return {
        type: 'recurring',
        prices: prices,
        currency: annualStripePrice?.currency ?? monthlyStripePrice?.currency ?? quarterlyStripePrice?.currency,
        coupons,
        trialDays,
        symbol: annualPrice?.symbol ?? monthlyPrice?.symbol ?? quarterlyPrice?.symbol,
        suffix: annualPrice?.suffix ?? monthlyPrice?.suffix ?? quarterlyPrice?.suffix,
        uiConfig: {
            displayCurrencySelector: oldPrices.display_currency_selector
        },
        composedPrices: {
            annual: annualPrice ? {
                price: annualPrice?.plan_price,
                priceWithDiscount: annualPrice?.plan_price_with_discount,
                pricePerMonth: annualPrice?.plan_price_per_month,
                pricePerMonthWithDiscount: annualPrice?.plan_price_per_month_with_discount,
                symbol: annualPrice?.symbol,
                suffix: annualPrice?.suffix,
                priceId: annualPrice?.price_id
            } : undefined,
            monthly: monthlyPrice ? {
                price: monthlyPrice?.plan_price,
                priceWithDiscount: monthlyPrice?.plan_price_with_discount,
                pricePerMonth: monthlyPrice?.plan_price_per_month,
                pricePerMonthWithDiscount: monthlyPrice?.plan_price_per_month_with_discount,
                symbol: monthlyPrice?.symbol,
                suffix: monthlyPrice?.suffix,
                priceId: monthlyPrice?.price_id
            } : undefined,
            quarterly: quarterlyPrice ? {
                price: quarterlyPrice?.plan_price,
                priceWithDiscount: quarterlyPrice?.plan_price_with_discount,
                pricePerMonth: quarterlyPrice?.plan_price_per_month,
                pricePerMonthWithDiscount: quarterlyPrice?.plan_price_per_month_with_discount,
                symbol: quarterlyPrice?.symbol,
                suffix: quarterlyPrice?.suffix,
                priceId: quarterlyPrice?.price_id
            } : undefined
        }
    };
};

export const findPriceIdByRecurringInterval = (
    composedPrices: ComposedRecurringPrices,
    interval: PriceRecurringInterval
): string => {
    const priceId = composedPrices[interval]?.priceId;
    return priceId;
};

const STRIPE_2022_COUPON_ID = '2022';
const STRIPE_CM_COUPON_ID = '9F9IUikh';
const STRIPE_CP_20_COUPON_ID = 'cp_20';
const STRIPE_IU_20_COUPON_ID = 'iu_20';
const STRIPE_SP_20_COUPON_ID = 'sp_20';

const couponCheckoutMap: Record<string, string> = {
    [CheckoutSessionType.CYBER_MONDAY]: STRIPE_CM_COUPON_ID,
    [CheckoutSessionType.NEW_YEAR_2022]: STRIPE_2022_COUPON_ID,
    [CheckoutSessionType.CP_20]: STRIPE_CP_20_COUPON_ID,
    [CheckoutSessionType.IU99_20]: STRIPE_IU_20_COUPON_ID,
    [CheckoutSessionType.SP99_Y_20]: STRIPE_SP_20_COUPON_ID,
    [CheckoutSessionType.CP99_20]: STRIPE_CP_20_COUPON_ID,
    [CheckoutSessionType.IU99_20_ALL]: STRIPE_IU_20_COUPON_ID
};

export const mapCheckoutTypeToCouponId = (checkoutType: string): string | undefined => {
    return couponCheckoutMap[checkoutType];
};

const composeCheckoutInfoStoragePayload = (checkoutInfo: TempCheckoutInfoSetPayload): TempCheckoutInfoLocalStorage => {
    const getCheckoutType = (checkoutType: string | string[]): CheckoutSessionType => {
        if (Array.isArray(checkoutType)) {
            return checkoutType[0] as CheckoutSessionType;
        }
        return checkoutType as CheckoutSessionType;
    };
    const checkoutType = getCheckoutType(checkoutInfo.checkout_type);
    const priceGroup = Array.isArray(checkoutInfo.price_group) ? checkoutInfo.price_group[0] : checkoutInfo.price_group;
    const coupons = Array.isArray(checkoutInfo.coupons) ? checkoutInfo.coupons[0] : checkoutInfo.coupons;
    const tier = Array.isArray(checkoutInfo.tier) ? checkoutInfo.tier[0] : checkoutInfo.tier;
    const priceVariant = Array.isArray(checkoutInfo.price_variant) ? checkoutInfo.price_variant[0] : checkoutInfo.price_variant;
    const premiumCoachingPriceGroup = Array.isArray(checkoutInfo.premium_coaching_price_group) ? checkoutInfo.premium_coaching_price_group[0] : checkoutInfo.premium_coaching_price_group;
    return {
        checkout_type: checkoutType,
        price_group: priceGroup,
        coupons,
        trial_days: checkoutInfo.trial_days,
        tier,
        price_variant: priceVariant,
        premium_coaching_price_group: premiumCoachingPriceGroup
    };
};

/**
 * This is temporary to replace the old checkoutData structure. It will be removed once we fully
 * migrate to the new payments infrastructure and we have "better" solution for this.
 */
export const getCheckoutInfoStorage = (): TempCheckoutInfoLocalStorage | undefined => {
    const checkoutInfo = LocalStorage.get('checkoutInfo');
    if (!checkoutInfo) {
        return;
    }
    try {
        return JSON.parse(checkoutInfo);
    } catch (error) {
        console.error('Error parsing checkoutInfo', error);
        return;
    }
};

export const setCheckoutInfoStorage = (checkoutInfo?: TempCheckoutInfoSetPayload) => {
    if (!checkoutInfo) {
        return;
    }
    const currentCheckoutInfo = getCheckoutInfoStorage();
    const composedCheckoutInfo = composeCheckoutInfoStoragePayload({
        ...currentCheckoutInfo,
        ...checkoutInfo
    });
    LocalStorage.set('checkoutInfo', JSON.stringify(composedCheckoutInfo));
};
